/**
 * @module cercaliagl/control/Draw
 */
// import MapboxglDraw from '@mapbox/mapbox-gl-draw';
import DRAW_POLYGON from '@mapbox/mapbox-gl-draw/src/modes/draw_polygon';
import StaticMode from '@mapbox/mapbox-gl-draw-static-mode';
import {
  SnapPolygonMode, SnapPointMode, SnapLineMode, SnapModeDrawStyles,
} from 'mapbox-gl-draw-snap-mode';
import { CircleMode, DragCircleMode } from 'mapbox-gl-draw-circle';
import Control from './Control';
import MapControls from './MapControls';
import { i18n } from '../dictionary';
import VControlButton from '../view/VControlButton';
import * as MapboxglDraw from '../../../plugins/mapbox-gl-draw.min.js';
import { checkPolygonSegmentIntersection, isMobile } from '../util';
import FreehandMode from '../interaction/FreehandDraw';

/**
 * @classdesc
 * This class permit to draw features.
 * It is possible to add custom callback functions.
 *
 * ```js
 * const drawControl = map.getControlByName(cercaliagl$$control$$MapControls.DRAW);
 * drawControl.getElement().style.display = 'none';
 * drawControl.onDrawEnd = (feature) => console.log('drawEnd', feature);
 * drawControl.onModify = (feature) => console.log('onModify', feature);
 * drawControl.onDelete = (feature) => console.log('onDelete', feature);
 * drawControl.onSelect = (features) => console.log('onSelect'), features);
 * drawControl.onDeselect = () => console.log('onDeselect');
 * ```
 *
 * @extends {Control}
 * @api
 */

const ADAPTED_STYLES = [

  {
    id: 'gl-draw-polygon-fill-active',
    type: 'fill',
    filter: [
      'all',
      [
        '==',
        'active',
        'true',
      ],
      [
        '==',
        '$type',
        'Polygon',
      ],
    ],
    paint: {
      'fill-color': ['coalesce', ['get', 'user_fillColor'], '#009BFF'],
      'fill-opacity': ['coalesce', ['get', 'user_fillOpacity'], 0.2],
      'fill-outline-color': 'transparent',
    },
  },
  {
    id: 'gl-draw-polygon-fill-inactive',
    type: 'fill',
    filter: [
      'all',
      [
        '==',
        'active',
        'false',
      ],
      [
        '==',
        '$type',
        'Polygon',
      ],
      [
        '!=',
        'mode',
        'static',
      ],
    ],
    paint: {
      'fill-color': ['coalesce', ['get', 'user_fillColor'], '#009BFF'],
      'fill-opacity': ['coalesce', ['get', 'user_fillOpacity'], 0.2],
      'fill-outline-color': 'transparent',
    },
  },
  {
    id: 'gl-draw-polygon-stroke-active',
    type: 'line',
    filter: [
      'all',
      [
        '==',
        'active',
        'true',
      ],
      [
        '==',
        '$type',
        'Polygon',
      ],
    ],
    paint: {
      'line-color': ['coalesce', ['get', 'user_strokeColor'], '#2196F3'],
      'line-width': ['coalesce', ['get', 'user_strokeWidth'], 7],
    },
  },
  {
    id: 'gl-draw-polygon-stroke-active-around',
    type: 'line',
    filter: [
      'all',
      [
        '==',
        'active',
        'true',
      ],
      [
        '==',
        '$type',
        'Polygon',
      ],
    ],
    layout: {
      'line-cap': 'round',
      'line-join': 'round',
    },
    paint: {
      'line-color': '#fbb03b',
      'line-dasharray': [
        0.2,
        2,
      ],
      'line-width': 2,
    },
  },
  {
    id: 'gl-draw-polygon-stroke-inactive',
    type: 'line',
    filter: [
      'all',
      [
        '==',
        'active',
        'false',
      ],
      [
        '==',
        '$type',
        'Polygon',
      ],
      [
        '!=',
        'mode',
        'static',
      ],
    ],
    paint: {
      'line-color': ['coalesce', ['get', 'user_strokeColor'], '#2196F3'],
      'line-width': ['coalesce', ['get', 'user_strokeWidth'], 7],
    },
  },
  {
    id: 'gl-draw-line-active-fill',
    type: 'line',
    filter: [
      'all',
      [
        '==',
        '$type',
        'LineString',
      ],
      [
        '==',
        'active',
        'true',
      ],
    ],
    paint: {
      'line-color': ['coalesce', ['get', 'user_strokeColor'], '#2196F3'],
      'line-width': ['coalesce', ['get', 'user_strokeWidth'], 7],
    },
  },
  {
    id: 'gl-draw-line-inactive',
    type: 'line',
    filter: [
      'all',
      [
        '==',
        'active',
        'false',
      ],
      [
        '==',
        '$type',
        'LineString',
      ],
      [
        '!=',
        'mode',
        'static',
      ],
      [
        '!=',
        'user_isSnapGuide',
        'true',
      ],
    ],
    paint: {
      'line-color': ['coalesce', ['get', 'user_strokeColor'], '#2196F3'],
      'line-width': ['coalesce', ['get', 'user_strokeWidth'], 7],
    },
  },
  {
    id: 'gl-draw-line-active',
    type: 'line',
    filter: [
      'all',
      [
        '==',
        '$type',
        'LineString',
      ],
      [
        '==',
        'active',
        'true',
      ],
    ],
    layout: {
      'line-cap': 'round',
      'line-join': 'round',
    },
    paint: {
      'line-color': '#fbb03b',
      'line-dasharray': [
        0.2,
        4,
      ],
      'line-width': 2,
    },
  },
  {
    id: 'gl-draw-line-static',
    type: 'line',
    filter: [
      'all',
      [
        '==',
        'mode',
        'static',
      ],
      [
        '==',
        '$type',
        'LineString',
      ],
    ],
    layout: {
      'line-cap': 'round',
      'line-join': 'round',
    },
    paint: {
      'line-color': ['coalesce', ['get', 'user_strokeColor'], '#2196F3'],
      'line-width': ['coalesce', ['get', 'user_strokeWidth'], 7],
    },
  },
  {
    id: 'gl-draw-polygon-and-line-vertex-inactive',
    type: 'circle',
    filter: [
      'all',
      [
        '==',
        'meta',
        'vertex',
      ],
      [
        '==',
        '$type',
        'Point',
      ],
      [
        '!=',
        'mode',
        'static',
      ],
    ],
    paint: {
      'circle-radius': ['coalesce', ['get', 'user_strokeWidth'], 7],
      'circle-color': '#fbb03b',
    },
  },
  {
    id: 'gl-draw-polygon-and-line-vertex-stroke-inactive',
    type: 'circle',
    filter: [
      'all',
      [
        '==',
        'meta',
        'vertex',
      ],
      [
        '==',
        '$type',
        'Point',
      ],
      [
        '!=',
        'mode',
        'static',
      ],
    ],
    paint: {
      'circle-radius': 5,
      'circle-color': '#fbb03b',
    },
  },
  {
    id: 'gl-draw-point-inactive',
    type: 'circle',
    filter: [
      'all',
      [
        '==',
        'active',
        'false',
      ],
      [
        '==',
        '$type',
        'Point',
      ],
      [
        '==',
        'meta',
        'feature',
      ],
      [
        '!=',
        'mode',
        'static',
      ],
    ],
    paint: {
      'circle-radius': ['coalesce', ['get', 'user_strokeWidth'], 7],
      'circle-color': '#fbb03b',
    },
  },
  {
    id: 'gl-draw-polygon-and-line-vertex-static',
    type: 'circle',
    filter: ['all', ['==', 'meta', 'vertex'], ['==', '$type', 'Point'], ['==', 'mode', 'static']],
    paint: {
      'circle-radius': ['coalesce', ['get', 'user_strokeWidth'], 7],
      'circle-color': '#fbb03b',
    },
  },
];

class Draw extends Control {
  /**
   * @constructor
   * @param {Object} options Options
   */
  constructor(options) {
    const controlButton = new VControlButton('draw');
    const element = controlButton.getElement();

    super({
      element,
      className: 'cercaliagl.control.Draw',
      name: MapControls.DRAW,
    });

    /**
     * @private
     * @type {VControlButton}
     */
    this.controlButton_ = controlButton;

    /**
     * @private
     * @type {HTMLElement}
     */
    this.element_ = element;

    /**
     * @private
     * @type {boolean}
     */
    this.enabled_ = false;

    /**
     * @private
     * @type {boolean}
     */
    this.snapEnabled_ = false;

    /**
     * Allow polygon intersections when drawing, true by default
     * @type {Boolean}
     * @api
     */
    this.allowPolygonIntersection = true;

    /**
     * @type {Function|null}
     * @api
     */
    this.onDrawEnd = null;

    /**
     * @type {Function|null}
     * @api
     */
    this.onDelete = null;

    /**
     * @type {Function|null}
     * @api
     */
    this.onModify = null;

    /**
     * @type {Function|null}
     * @api
     */
    this.onSelect = null;

    /**
     * @type {Function|null}
     * @api
     */
    this.onDeselect = null;

    /**
     * @private
     * @type {Element}
     */
    this.container_ = null;

    /**
     * @public
     * @type {boolean}
     */
    this.disableDragWhenEditing_ = false;

    /**
     * @private
     * @type {string}
     */
    this.simpleSelectMode_ = this.disableDragWhenEditing_ ? 'simple_select_without_draw' : 'simple_select';

    /**
     * @private
     * @type {string}
     */
    this.directSelectMode_ = this.disableDragWhenEditing_ ? 'direct_select_without_draw' : 'direct_select';

    /**
     * @private
     * @type {boolean}
     */
    this.adaptDrawStylesToFeatureStyle_ = options.adaptDrawStylesToFeatureStyle || false;

    this.selectedFeature = null;

    /**
     * @private
     * @type {MapboxglDraw}
     */
    this.mbDrawControl_ = new MapboxglDraw({
      modes: {
        ...MapboxglDraw.modes,
        simple_select_without_draw: { ...MapboxglDraw.modes.simple_select, dragMove() {} },
        direct_select_without_draw: { ...MapboxglDraw.modes.direct_select, dragFeature() {} },
        static: StaticMode,
        draw_point: SnapPointMode,
        draw_polygon: isMobile() && options.freehandOnDrawPolygonMobile ? FreehandMode : SnapPolygonMode,
        freeHandPolygonMode: FreehandMode,
        draw_magnetic_polygon: DRAW_POLYGON,
        draw_line_string: SnapLineMode,
        draw_circle: CircleMode,
        drag_circle: DragCircleMode,
      },
      controls: {
        line_string: true,
        polygon: true,
        trash: true,
      },
      styles: [
        ...this.getFinalStyles(options.styles),
      ],
      userProperties: true,
      // Config snapping features
      snap: this.snapEnabled_,
      snapOptions: {
        snapPx: 15, // defaults to 15
        snapToMidPoints: true, // defaults to false
      },
      guides: false,
      displayControlsDefault: false,
    });
    const onAddOld = this.mbDrawControl_.onAdd;
    this.mbDrawControl_.onAdd = (e) => {
      this.container_ = onAddOld(e);
      this.container_.classList.add('cercaliagl-draw');
      this.container_.style.display = 'none';

      // Add snap button
      const snapBtn = document.createElement('button');
      snapBtn.className = 'mapbox-gl-draw_ctrl-draw-btn mapbox-gl-draw_snap';
      snapBtn.setAttribute('title', 'Snap');
      snapBtn.addEventListener('click', () => this.setSnapping(!this.snapEnabled_));
      this.container_.appendChild(snapBtn);

      // Mode select
      const selectBtn = document.createElement('button');
      selectBtn.className = 'mapbox-gl-draw_ctrl-draw-btn select-mode mapbox-gl-draw_select';
      selectBtn.setAttribute('title', 'Select feature');
      selectBtn.addEventListener('click', () => this.toggleSelectMode_());
      this.container_.appendChild(selectBtn);

      return this.container_;
    };
  }

  /**
   * Initialize
   */
  initialize() {
    this.controlButton_.setTooltip(i18n('tooltipDraw'));
    this.map_.addControl(this.mbDrawControl_, 'top-right');
    this.mbDrawControl_.changeMode('static');

    if (this.map_.boxZoom) {
      this.map_.boxZoom.enable();
    }

    this.element_.addEventListener('click', () => {
      if (this.enabled_) {
        this.disable();
      } else {
        this.enable();
      }
    });

    // Events drawing
    this.map_.on('draw.create', this.handleDrawEnd_.bind(this));
    this.map_.on('draw.delete', this.handleDelete_.bind(this));
    this.map_.on('draw.update', this.handleUpdate_.bind(this));
    this.map_.on('draw.selectionchange', this.handleSelectionChange_.bind(this));
  }

  /**
   * @private
   * @param {import('@mapbox/mapbox-gl-draw').DrawCreateEvent} e Event
   */
  handleDrawEnd_(e) {
    if (this.checkIfPolygonDrawedOrModifiedCanNotBeDrawn_(e)) {
      this.mbDrawControl_.delete(e.features[0].id);
    } else if (this.onDrawEnd) {
      this.onDrawEnd(e.features[0]);
    }
  }

  /**
   * @private
   * @param {import('@mapbox/mapbox-gl-draw').DrawCreateEvent} e Event
   */
  // eslint-disable-next-line class-methods-use-this
  getFinalStyles(styles) {
    let finalStyles = [];
    if (styles && styles.length === 0 && !this.adaptDrawStylesToFeatureStyle_) {
      finalStyles = SnapModeDrawStyles;
    } else {
      SnapModeDrawStyles.forEach((snapStyle) => {
        if (!styles.some((style) => style.id === snapStyle.id) && (!this.adaptDrawStylesToFeatureStyle_ || !ADAPTED_STYLES.some((style) => style.id === snapStyle.id))) {
          finalStyles.push(snapStyle);
        }
      });
      if (this.adaptDrawStylesToFeatureStyle_) {
        finalStyles.push(...ADAPTED_STYLES);
      }
      finalStyles.push(...styles);
    }
    return finalStyles;
  }

  /**
   * @private
   * @param {import('@mapbox/mapbox-gl-draw').DrawDeleteEvent} e Event
   */
  handleDelete_(e) {
    if (this.onDelete) {
      this.onDelete(e.features);
    }
  }

  /**
   * @private
   * @param {import('@mapbox/mapbox-gl-draw').DrawUpdateEvent} e Event
   */
  handleUpdate_(e) {
    if (this.checkIfPolygonDrawedOrModifiedCanNotBeDrawn_(e)) {
      this.mbDrawControl_.delete(e.features[0].id);
    } else if (this.onModify) {
      this.onModify(e.features[0]);
    }
  }

  /**
   * @param {import('@mapbox/mapbox-gl-draw').DrawCreateEvent | import('@mapbox/mapbox-gl-draw').DrawUpdateEvent} e Event
   * @returns {boolean} True if polygon can not be drawn
  */
  checkIfPolygonDrawedOrModifiedCanNotBeDrawn_(e) {
    return (e && e.features.length > 0
    && e.features[0].geometry.type === 'Polygon') && ((!this.allowPolygonIntersection && checkPolygonSegmentIntersection(e.features[0]))
    || (e.features.length === 1 && e.features[0].geometry.coordinates[0] < 4)); // Not multipolygon and less than 4 points
  }

  /**
   * @private
   * @param {import('@mapbox/mapbox-gl-draw').DrawSelectionChangeEvent} e Event
   */
  handleSelectionChange_(e) {
    if (e.features.length > 0 && this.onSelect) {
      this.onSelect(e.features);
    } else {
      if (this.selectedFeature && e.features.length === 0) {
        const featureId = this.selectedFeature.getId();
        const geojson = this.mbDrawControl_.get(featureId);
        this.selectedFeature.setVisible(true);
        this.selectedFeature.setGeometry(geojson.geometry);
        if (this.selectedFeature.onModifyEnd) {
          this.selectedFeature.onModifyEnd(this.selectedFeature);
        }
        const internalFeature = this.mbDrawControl_.get(featureId);
        internalFeature.properties.zIndex = -1;
        internalFeature.properties.opacity = 0;
        this.mbDrawControl_.changeMode('static');
        this.mbDrawControl_.changeMode(this.simpleSelectMode_);
        this.selectedFeature = null;
        setTimeout(() => {
          this.mbDrawControl_.delete(featureId);
        }, 100);
      }
      if (this.onDeselect) {
        this.onDeselect();
      }
    }
  }

  /**
   * Load Cercalia Features to draw control. Then you can modify them or delete.
   * @param {Array.<Feature>} features Features
   * @api
   */
  loadFeatures(features) {
    features.forEach((feature) => {
      this.mbDrawControl_.add(feature.getGeoJSON());
    });
  }

  /**
   * Set disable drag when editing
   * @param {boolean} disable Disable
   * @api
   */
  setDisableDragWhenEditing(disable) {
    this.disableDragWhenEditing_ = disable;
    this.simpleSelectMode_ = this.disableDragWhenEditing_ ? 'simple_select_without_draw' : 'simple_select';
    this.directSelectMode_ = this.disableDragWhenEditing_ ? 'direct_select_without_draw' : 'direct_select';
    const currentMode = this.mbDrawControl_?.getMode();
    if (!currentMode) {
      return;
    }
    if (currentMode === 'simple_select' || currentMode === 'simple_select_without_draw') {
      this.mbDrawControl_.changeMode(this.simpleSelectMode_);
    } else if ((currentMode === 'direct_select' || currentMode === 'direct_select_without_draw') && this.selectedFeature) {
      this.mbDrawControl_.changeMode(this.directSelectMode_, { featureId: this.selectedFeature.getId() });
    }
  }

  /**
   * Get current mode
   * @api
   */
  getCurrentMode() {
    return this.mbDrawControl_.getMode();
  }

  /**
     * Sets a property on the given feature
     * @param {string} featureId
     * @param {string} property property
     * @param {string} value value
     * @api
    */
  setFeatureProperty(featureId, property, value) {
    this.mbDrawControl_.setFeatureProperty(featureId, property, value);
    this.mbDrawControl_.changeMode(this.simpleSelectMode_, { featureIds: [featureId] });
    this.mbDrawControl_.changeMode(this.directSelectMode_, { featureId });
  }

  /**
   * Load features via GeoJSON or geometry
   * @param {GeoJSON} geojson GeoJSON
   * @api
   */
  loadGeoJSON(geojson) {
    this.mbDrawControl_.add(geojson);
  }

  /**
   * Delete feature by id
   * @param {string} id Feature id
   * @api
   */
  deleteFeature(id) {
    this.mbDrawControl_.delete(id);
  }

  /**
   * Return if polygon intersections are allowed
   * @return {boolean} allow Allow
   * @api
  */
  getAllowPolygonIntersections() {
    return this.allowPolygonIntersection;
  }

  /**
   * Set if polygon intersections are allowed
   * @param {boolean} allow Allow
   * @api
  */
  setAllowPolygonIntersections(allow) {
    this.allowPolygonIntersection = allow;
  }

  /**
   * Return feature in `GeoJSON` format by feature id.
   * @param {string} id Feature Id
   * @return {GeoJSON} feature Feature
   * @api
   */
  getFeatureById(id) {
    return this.mbDrawControl_.getFeatureById(id);
  }

  /**
   * Return all drawing features in `GeoJSON` format.
   * @return {Array.<GeoJSON>}
   * @api
   */
  getGeoJSONFeatures() {
    return this.mbDrawControl_.getAll().features;
  }

  /**
   * Enable or disable snapping
   * @param {boolean} enabled Enabled
   * @api
   */
  setSnapping(enabled) {
    this.snapEnabled_ = enabled;
    this.mbDrawControl_.options.snap = this.snapEnabled_;
    if (this.snapEnabled_) {
      this.container_.querySelector('.mapbox-gl-draw_snap').classList.add('active');
    } else {
      this.container_.querySelector('.mapbox-gl-draw_snap').classList.remove('active');
    }
  }

  /**
   * @private
   */
  toggleSelectMode_() {
    if (this.mbDrawControl_.getMode() === this.simpleSelectMode_) {
      this.setStaticMode();
    } else {
      this.setSelectMode();
    }
  }

  /**
   * Enable select mode to select features. After select it is possible to delete features. Hit and hold `Shift` key if you want
   * to select more features.
   * @api
   */
  setSelectMode() {
    this.mbDrawControl_.changeMode(this.simpleSelectMode_);
    const selectModeElem = this.container_.querySelector('.select-mode');
    selectModeElem.classList.remove('mapbox-gl-draw_select');
    selectModeElem.classList.add('mapbox-gl-draw_static');
  }

  /**
   * Static mode shows drawn features but in this mode it is not possible to edit them.
   * @api
   */
  setStaticMode() {
    this.mbDrawControl_.changeMode('static');
    const selectModeElem = this.container_.querySelector('.select-mode');
    selectModeElem.classList.remove('mapbox-gl-draw_static');
    selectModeElem.classList.add('mapbox-gl-draw_select');
  }

  /**
   * Enable draw linestring interaction
   * @api
   */
  setDrawLinestring() {
    this.setStaticMode();
    this.container_.querySelector('.mapbox-gl-draw_line').click();
  }

  /**
   * Select feature
   * @param {import("../Feature").default} feature Feature
   */
  select(feature) {
    if (this.selectedFeature) {
      this.unselect(this.selectedFeature);
      this.selectedFeature = null;
    }
    const geojson = feature.getGeoJSON();
    geojson.id = feature.getId();
    this.mbDrawControl_.add(geojson);
    this.mbDrawControl_.changeMode(this.directSelectMode_, { featureId: geojson.id });
    if (feature.onModifyStart) {
      feature.onModifyStart(feature);
    }
    this.selectedFeature = feature;
    // setTimeout(() => this.map_.once('click', () => this.unselect(feature)), 100);
  }

  /**
   * @param {import("../Feature").default} feature Feature
   * @param {GeoJSON} feature Feature
   */
  unselect(feature) {
    const featureId = feature.getId();
    const geojson = this.mbDrawControl_.get(featureId);
    feature.setVisible(true);
    feature.setGeometry(geojson.geometry);

    if (feature.onModifyEnd) {
      feature.onModifyEnd(feature);
    }
    this.hideFeature_(featureId);
    this.mbDrawControl_.delete(featureId);
  }

  /**
   * @private
   * @param {string} featureId FeatureId
   */
  hideFeature_(featureId) {
    this.mbDrawControl_.setFeatureProperty(featureId, 'show', false);
    this.mbDrawControl_.add(this.mbDrawControl_.get(featureId));
    this.mbDrawControl_.changeMode(this.simpleSelectMode_, { featureId: this.mbDrawControl_.get(featureId).id });
  }

  /**
   * Enable draw polygon interaction
   * @api
   */
  setDrawPolygonMode() {
    this.setStaticMode();
    this.container_.querySelector('.mapbox-gl-draw_polygon').click();
  }

  /**
   * Enable draw polygon interaction
   * @api
   */
  setDrawFreehandPolygonMode() {
    this.setStaticMode();
    this.mbDrawControl_.changeMode('freeHandPolygonMode');
  }

  /**
   * Enable draw polygon interaction with magnetic closes
   * @api
   */
  setDrawMagneticPolygonMode() {
    this.setStaticMode();
    this.mbDrawControl_.changeMode('draw_magnetic_polygon');
  }

  /**
   * Enable draw polygon interaction
   * @api
   */
  setDrawCircleMode() {
    this.setStaticMode();
    this.mbDrawControl_.changeMode('drag_circle', { initialRadiusInKm: 0.5 });
  }

  /**
   * Enable control. Show UI buttons.
   * @api
   */
  enable() {
    this.container_.style.display = 'block';
    this.enabled_ = true;
  }

  /**
   * Disable control. Show UI buttons.
   * @api
   */
  disable() {
    this.container_.style.display = 'none';
    this.enabled_ = false;
  }

  /**
   * Delete all drawn features
   * @api
   */
  deleteAllFeatures() {
    this.mbDrawControl_.deleteAll();
  }
}

export default Draw;
